import { createStyles, Grid, TextField, Theme, Typography, WithStyles, withStyles } from '@material-ui/core'
import * as R from 'ramda'
import * as React from 'react'

import { sortStrings } from '../../../services/sortingService'
import { Nullable } from '../../../services/utils'
import CustomMultiSelectDropDown from '../CustomMultiSelectDropDown/CustomMultiSelectDropDown'

interface EnrollmentInformationProps extends WithStyles<typeof styles> {
  totalParticipantsEnrolled: Nullable<number>
  onTotalParticipantsChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  schoolNames: Nullable<string>
  onSchoolNameChange: (joinedSchools: string) => void
  currentGrade: Nullable<number>
  onCurrentGradeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
}

const SCHOOL_LIST = sortStrings([
  "Brown University",
  "Bryant University",
  "Community College of Rhode Island",
  "Johnson & Wales University",
  "Naval War College",
  "New England Institute of Technology",
  "Providence College",
  "Rhode Island College",
  "Rhode Island School of Design",
  "Roger Williams University",
  "Salve Regina University",
  "University of Rhode Island",
  "360 High School",
  "Academy for Career Exploration(ACES)",
  "AF Providence Mayoral Academy Middle",
  "Alan Shawn Feinstein Middle School Of Coventry",
  "Archie R.Cole Middle School",
  "Barrington High School",
  "Barrington Middle School",
  "BEACON Charter School",
  "Birchwood Middle School",
  "Blackstone Academy Charter School",
  "Blackstone Valley Prep High School",
  "Blackstone Valley Prep Middle School",
  "Blackstone Valley Prep Middle School 2",
  "Block Island School",
  "Broad Rock Middle School",
  "Burrillville High School",
  "Burrillville Middle School",
  "Central Falls Senior High School",
  "Central High School",
  "Chariho Area Career and Technical Center",
  "Chariho Regional High School",
  "Chariho Regional Middle School",
  "Charles E.Shea High School",
  "Classical High School",
  "Coventry High School",
  "Cranston Area Career Technical Center",
  "Cranston High School East",
  "Cranston High School West",
  "Cumberland High School",
  "Curtis Corner Middle School",
  "Davisville Middle School",
  "DCYF Alternative Education Program",
  "Dr.Edward A.Ricci Middle School",
  "Dr.Jorge Alvarez High School",
  "E.Providence Area Career and Technical Center",
  "East Greenwich High School",
  "East Providence High School",
  "E - Cubed Academy",
  "Edward R.Martin Middle School",
  "Esek Hopkins Middle School",
  "Evolutions High School",
  "Exeter - West Greenwich Regional  Junior High",
  "Exeter - West Greenwich Regional High School",
  "Founders Academy",
  "Frank E.Thompson Middle School",
  "Gilbert Stuart Middle School",
  "Governor Christopher DelSesto Middle School",
  "Hope High School",
  "Hope Highlands Middle School",
  "Hugh B.Bain Middle School",
  "Jacqueline M.Walsh School for the Performing and",
  "Jamestown School - Lawn",
  "John F.Deering Middle School",
  "Johnston Senior High School",
  "Joseph Jenks Middle School",
  "Joseph L.McCourt Middle School",
  "Kickemuit Middle School",
  "Lincoln Middle School",
  "Lincoln Senior High School",
  "Lyman B.Goff Middle School",
  "Metropolitan Regional Career and Technical Center",
  "Middletown High School",
  "Mount Pleasant High School",
  "Mt.Hope High School",
  "Narragansett High School",
  "Narragansett Pier School",
  "Nathan Bishop Middle School",
  "Nathanael Greene Middle School",
  "NEL / CPS Construction Career Academy",
  "Newport Area Career and Technical Center",
  "Nicholas A.Ferri Middle School",
  "North Cumberland Middle School",
  "North Kingstown Senior High School",
  "North Providence High School",
  "North Smithfield High School",
  "North Smithfield Middle School",
  "Park View Middle School",
  "Pilgrim High School",
  "Ponaganset High School",
  "Ponaganset Middle School",
  "Portsmouth High School",
  "Portsmouth Middle School",
  "Providence Career and Technical Academy",
  "Rhode Island School for the Deaf",
  "RI Nurses Institute Middle College Charter High Sc",
  "RISE Prep Mayoral Academy",
  "Riverside Middle School",
  "Roger Williams Middle School",
  "Rogers High School",
  "Samuel Slater Middle School",
  "Scituate High School",
  "Scituate Middle School",
  "Segue Institute for Learning",
  "Sheila Skip Nowell Leadership Academy(Capital Cam)",
  "Sheila Skip Nowell Leadership Academy(Central Cam)",
  "Smithfield Senior High School",
  "South Kingstown High School",
  "The Greene School",
  "The R.Y.S.E.School",
  "The Regional Career and Technical Center at Covent",
  "Times2 Academy",
  "Tiverton High School",
  "Tiverton Middle School",
  "Toll Gate High School",
  "Trinity Academy for the Performing Arts",
  "Urban Collaborative Accelerated Program",
  "Village Green Virtual Charter School",
  "Vincent J.Gallagher Middle School",
  "Warwick Area Career and Technical Center",
  "Warwick Veterans Jr.High School",
  "West Broadway Middle School",
  "West Warwick Senior High School",
  "Westerly High School",
  "Westerly Middle School",
  "Western Hills Middle School",
  "Wickford Middle School",
  "William B.Cooley, Sr.High School and the Provide",
  "William E Tolman Senior High School",
  "Winman Junior High School",
  "Wm.M.Davies Jr.Career - Technical  High School",
  "Woonsocket Area Career and Technical Center",
  "Woonsocket High School",
  "Woonsocket Middle School at Hamlet",
  "Woonsocket Middle School at Villa Nova",
])

const populateSchools = (schoolsSTR: string) => {
  return R.map(R.trim, R.split(',', schoolsSTR || ''))
}

const EnrollmentInformation: React.FC<EnrollmentInformationProps> = (props: EnrollmentInformationProps) => {
  const {
    classes,
    currentGrade,
    onCurrentGradeChange,
    onSchoolNameChange,
    onTotalParticipantsChange,
    schoolNames,
    totalParticipantsEnrolled
  } = props

  const [allSchoolNames, setSchoolNames] = React.useState(SCHOOL_LIST)
  const [schoolNamesArray, setSchoolNamesArray] = React.useState(
    schoolNames ? sortStrings(populateSchools(schoolNames)) : []
  )

  const onSchoolsSelect = (schools: string[]) => {
    onSchoolNameChange(R.join(',', sortStrings(R.uniq(schools))))
  }

  React.useEffect(() => {
    if (props.schoolNames === null) return

    const mergedNames = R.uniq([...schoolNamesArray, ...SCHOOL_LIST])

    if (mergedNames.length !== SCHOOL_LIST.length) {
      setSchoolNames(sortStrings(mergedNames))
    }

    setSchoolNamesArray(populateSchools(schoolNames || '').filter(str => !!str))

  }, [props.schoolNames, schoolNames, schoolNamesArray]);

  return (
    <section className={classes.root}>
      <Grid container={true} spacing={8} alignItems="center">
        <Grid item={true} xs={12}>
          <Typography variant="h5" className={classes.sectionSubtitle}>
            Enrollment Information
          </Typography>
        </Grid>
        <Grid item={true} xs={12} sm={6} md={3} className={classes.label}>Participants Enrolled&#58;</Grid>
        <Grid item={true} xs={12} sm={6} md={9}>
          <TextField
            required={true}
            value={totalParticipantsEnrolled || ''}
            id={"totalParticipantsEnrolled"}
            className={classes.textField}
            placeholder={"0"}
            margin="normal"
            variant="outlined"
            InputProps={{
              inputProps: {
                title: `Please Provide A Valid number.`,
                className: classes.input,
                name: "totalParticipantsEnrolled",
                type: "number",
                min: '0',
                autoFocus: true
              }
            }}
            onChange={onTotalParticipantsChange}
          />
        </Grid>

        <Grid item={true} xs={12}>
          <CustomMultiSelectDropDown
            fullList={allSchoolNames}
            currentValues={schoolNamesArray}
            onSchoolsSelect={onSchoolsSelect}
          />
        </Grid>
        <Grid item={true} xs={12} sm={6} md={3} className={classes.label}>
          Current / Entering Grade, (if applicable)&#58;</Grid>
        <Grid item={true} xs={12} sm={6} md={9}>
          <TextField
            required={true}
            value={currentGrade || ''}
            id={"currentGrade"}
            className={classes.textField}
            placeholder={"0"}
            margin="normal"
            variant="outlined"
            InputProps={{
              inputProps: {
                title: `Please Provide A Valid grade.`,
                className: classes.input,
                name: "currentGrade",
                type: "number",
                min: '0',
                max: '12',
              }
            }}
            onChange={onCurrentGradeChange}
          />
        </Grid>
      </Grid>
    </section>
  )
}

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    textField: {
      minWidth: "35%",
      [theme.breakpoints.down('sm')]: {
        minWidth: "50%",
      },
    },
    input: {},
    label: {
      justifyContent: 'flex-end',
      display: 'flex',
      paddingRight: '8px',
      alignItems: 'center',

      [theme.breakpoints.down('sm')]: {
        flexBasis: '100%',
        justifyContent: 'flex-start',
      },
    },
    sectionSubtitle: {}
  })

export default withStyles(styles)(EnrollmentInformation)
